/**
 * How the user entered the hotel funnel from an offer surface — set at the offer
 * surface, read by hotel_entry / hotel_complete_buy. In sessionStorage (per-tab,
 * so it can't leak into a later session); the new-tab shop is bridged by the
 * `offer_source` URL param via captureOfferFunnelEntrySourceFromParams.
 *
 * Only the entry source is stored — offer_name / offer_type are derived from live
 * signals (3X eligibility, purchased-rate strikethrough) at each event.
 */

const STORAGE_KEY = "rbc_offer_funnel_entry_source";

/** URL param that carries the offer entry source across a new-tab boundary. */
export const OFFER_SOURCE_PARAM = "offer_source";

/** How the user entered the hotel funnel from an offer surface. */
export enum OfferFunnelEntrySource {
  offer_page = "offer_page",
  offer_banner = "offer_banner",
  wallet_offer = "wallet_offer",
  all_offers_offer = "all_offers_offer",
}

const isOfferFunnelEntrySource = (
  value: string | null
): value is OfferFunnelEntrySource =>
  value != null &&
  (Object.values(OfferFunnelEntrySource) as string[]).includes(value);

export const setOfferFunnelEntrySource = (
  source: OfferFunnelEntrySource
): void => {
  try {
    // First entry wins — don't overwrite an existing source, so an offers-page
    // entry isn't replaced by a later banner tap in the same session. Reset
    // happens only on homepage return.
    if (sessionStorage.getItem(STORAGE_KEY) != null) return;
    sessionStorage.setItem(STORAGE_KEY, source);
  } catch {
    // sessionStorage can be unavailable (private mode); this is best-effort.
  }
};

export const getOfferFunnelEntrySource = ():
  | OfferFunnelEntrySource
  | undefined => {
  try {
    const source = sessionStorage.getItem(STORAGE_KEY);
    return isOfferFunnelEntrySource(source) ? source : undefined;
  } catch {
    return undefined;
  }
};

export const clearOfferFunnelEntrySource = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
};

/**
 * Persist the entry source from an `offer_source` URL param when present and
 * recognized. Write-only: a missing/unknown param leaves any existing value
 * untouched. Freshness comes from per-tab sessionStorage plus the homepage clear.
 */
export const captureOfferFunnelEntrySourceFromParams = (
  params: URLSearchParams
): void => {
  const source = params.get(OFFER_SOURCE_PARAM);
  if (!isOfferFunnelEntrySource(source)) return;
  setOfferFunnelEntrySource(source);
};
